import { useMemo, useState, useEffect, lazy, Suspense } from 'react';
import type { Loch } from '@/app/lib/types';
import { Skeleton } from '@/components/ui/skeleton';

const InteractiveMap = lazy(() => import('@/components/map/InteractiveMap').then(mod => ({ default: mod.InteractiveMap })));


export default function MapPage() {
  const [locations, setLocations] = useState<Loch[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    async function fetchAllLochs() {
      setIsLoading(true);
      try {
        const response = await fetch('https://ai-travel-itinerary-ai-228543204937.europe-west2.run.app/api/get_lochs');
        if (!response.ok) {
          throw new Error('Failed to fetch lochs');
        }
        const data = await response.json();
        const supplementedData = data.map((loch: any) => ({
          ...loch,
          slug: loch.id,
          name: loch['Loch Name'],
          region: loch['County/Region'],
        }));
        setLocations(supplementedData);
      } catch (error) {
        console.error("Failed to fetch all lochs for map:", error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchAllLochs();
  }, []);

  const mapLocations = useMemo(() => locations.map(loc => ({
    id: loc.id,
    name: loc.name,
    slug: loc.slug,
    latitude: loc.Latitude,
    longitude: loc.Longitude,
  })), [locations]);

  return (
    <div className="container mx-auto h-[calc(100vh-8rem)] sm:h-[calc(100vh-9rem)] md:h-[calc(100vh-10rem)] flex flex-col">
      <div className="mb-4 sm:mb-6 md:mb-8 text-center">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">Interactive Map</h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-1 sm:mt-2 max-w-2xl mx-auto">
          Discover your next swim. Click the pins to see location names and plan your route across Scotland.
        </p>
      </div>
      <div className="flex-grow rounded-md sm:rounded-lg overflow-hidden shadow-md sm:shadow-lg border">
        {isLoading ? (
          <Skeleton className="w-full h-full bg-muted" />
        ) : (
          <Suspense fallback={<Skeleton className="w-full h-full bg-muted" />}>
            <InteractiveMap locations={mapLocations} />
          </Suspense>
        )}
      </div>
    </div>
  );
}
